import {
  ActionButton,
  Button,
  Chip,
  Container,
  Icon,
  Panel,
  SearchPagination,
  Table,
  TableChild,
  Tabs,
  usePagedQuery,
  useShowNotification
} from '@elotech/components';
import { Informativo, TableContainer } from 'common/components';
import { useExpandedIds } from 'common/hooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AnaliseInconsistenciaService from '../../service/AnaliseInconsistenciaService';
import {
  AnaliseInconsistenciaRegistro,
  AnaliseInconsistenciaResumo,
  AnaliseInconsistenciaTipo
} from '../../type';
import { RegraInconsistenciaComponent } from './RegraInconsistenciaComponent';
import { RegrasInconsistenciaListComponent } from './RegrasInconsistenciaListComponent';
import {
  SeveridadeChip,
  severidadeClassName,
  severidadeCor
} from './SeveridadeChip';

type DetalhesProps = {
  service: AnaliseInconsistenciaService;
  regraId: string;
  visible: boolean;
};

const DetalhesOcorrencia = ({ service, regraId, visible }: DetalhesProps) => {
  const { loading, values, pagination, doSearch, doPagedSearch } =
    usePagedQuery<AnaliseInconsistenciaRegistro, string>({
      search: service.detalhar
    });

  useEffect(() => {
    if (visible) {
      doSearch(regraId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <TableChild>
      <TableContainer>
        <Table<AnaliseInconsistenciaRegistro>
          values={values}
          loading={loading}
          keyExtractor={(registro, index) => `${registro.id}-${index}`}
          messageEmpty="Nenhum registro encontrado para essa ocorrência."
        >
          <Table.Column
            header="Registro"
            value={(registro: AnaliseInconsistenciaRegistro) =>
              registro.pathTela ? (
                <Link to={registro.pathTela}>{registro.descricaoRegistro}</Link>
              ) : (
                registro.descricaoRegistro
              )
            }
          />
        </Table>
        {pagination && pagination.totalPages > 0 && (
          <SearchPagination page={pagination} searchWithPage={doPagedSearch} />
        )}
      </TableContainer>
    </TableChild>
  );
};

type Props = {
  service: AnaliseInconsistenciaService;
  descricaoModulo: string;
};

type AnaliseProps = Props & {
  versaoRegras: number;
};

const AnaliseInconsistenciaTab = ({
  service,
  descricaoModulo,
  versaoRegras
}: AnaliseProps) => {
  const [tipos, setTipos] = useState<AnaliseInconsistenciaTipo[]>([]);
  const [tiposSelecionados, setTiposSelecionados] = useState<string[]>([]);
  const [carregandoTipos, setCarregandoTipos] = useState(true);
  const [resumo, setResumo] = useState<AnaliseInconsistenciaResumo[]>([]);
  const [analiseRealizada, setAnaliseRealizada] = useState(false);
  const [tiposAnalisados, setTiposAnalisados] = useState(0);
  const [loading, setLoading] = useState(false);
  const { expandedIds, handleExpandedIds } = useExpandedIds<string>();
  const showNotification = useShowNotification();

  useEffect(() => {
    service
      .tipos()
      .then(({ data }) => {
        setTipos(data);
        setTiposSelecionados(data.map(tipo => tipo.regraId));
      })
      .catch(() => {
        showNotification({
          level: 'error',
          message: 'Não foi possível carregar os tipos de ocorrência.'
        });
      })
      .finally(() => setCarregandoTipos(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versaoRegras]);

  const executarAnalise = () => {
    setLoading(true);
    setAnaliseRealizada(false);

    service
      .resumo(tiposSelecionados)
      .then(({ data }) => {
        setResumo(data);
        setTiposAnalisados(tiposSelecionados.length);
        setAnaliseRealizada(true);
      })
      .catch(() => {
        showNotification({
          level: 'error',
          message: 'Não foi possível executar a análise de inconsistências.'
        });
      })
      .finally(() => setLoading(false));
  };

  const toggleTipo = (regraId: string) => {
    setTiposSelecionados(atuais =>
      atuais.includes(regraId)
        ? atuais.filter(id => id !== regraId)
        : [...atuais, regraId]
    );
  };

  const quantidadeTotal = resumo
    .filter(regra => regra.severidade !== 'INFO')
    .reduce((total, regra) => total + (regra.quantidade || 0), 0);

  return (
    <>
      {!carregandoTipos && tipos.length === 0 && (
        <Informativo
          type="WARNING"
          withIcon
          title="Nenhuma regra ativa cadastrada"
        >
          Não há regras de análise ativas para este módulo. Cadastre ou ative
          regras para executar a análise.
        </Informativo>
      )}

      {tipos.length > 0 && !analiseRealizada && (
        <Informativo
          type="INFO"
          classes="mb-xs"
          withIcon
          title="Clique em “Analisar dados” para iniciar"
        >
          {`Confira a integridade do ${descricaoModulo}. Somente os tipos de ocorrência selecionados serão analisados.`}
        </Informativo>
      )}

      {tipos.length > 0 && analiseRealizada && (
        <Informativo
          type={quantidadeTotal === 0 ? 'SUCCESS' : 'WARNING'}
          withIcon
          classes="mb-xs"
          title={
            quantidadeTotal === 0
              ? 'Nenhuma inconsistência encontrada'
              : `${quantidadeTotal} ${
                  quantidadeTotal === 1
                    ? 'inconsistência encontrada'
                    : 'inconsistências encontradas'
                }`
          }
        >
          {`Resultado considerando ${tiposAnalisados} ${
            tiposAnalisados === 1 ? 'tipo de ocorrência' : 'tipos de ocorrência'
          }.`}
        </Informativo>
      )}

      <Panel>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '12px 0'
          }}
        >
          <div
            role="group"
            aria-label="Tipos de ocorrência"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}
          >
            {tipos.map(tipo => {
              const selecionado = tiposSelecionados.includes(tipo.regraId);

              return (
                <Chip
                  key={tipo.regraId}
                  color={
                    selecionado ? severidadeCor(tipo.severidade) : 'neutral'
                  }
                  className={
                    selecionado
                      ? severidadeClassName(tipo.severidade)
                      : undefined
                  }
                  outlined={!selecionado}
                  interactive
                  inline
                  iconLeft={selecionado ? 'check' : undefined}
                  value={tipo.descricao}
                  onClick={() => toggleTipo(tipo.regraId)}
                />
              );
            })}
          </div>
          <Button
            type="button"
            iconPosition="left"
            disabled={
              loading || carregandoTipos || tiposSelecionados.length === 0
            }
            onClick={executarAnalise}
          >
            {loading ? <Icon icon="spinner" spin /> : <Icon icon="search" />}{' '}
            {loading ? 'Analisando dados...' : 'Analisar dados'}
          </Button>
        </div>
        {tipos.length > 0 && tiposSelecionados.length === 0 && (
          <small className="typography-caption help-message">
            Selecione ao menos um tipo de ocorrência para analisar os dados.
          </small>
        )}
      </Panel>

      {analiseRealizada && (
        <>
          {resumo.length > 0 && (
            <Panel isTable>
              <Table<AnaliseInconsistenciaResumo>
                values={resumo}
                keyExtractor={(regra: AnaliseInconsistenciaResumo) =>
                  regra.regraId
                }
                messageEmpty="Nenhuma ocorrência encontrada."
                renderInnerComponent={(regra: AnaliseInconsistenciaResumo) => (
                  <DetalhesOcorrencia
                    service={service}
                    regraId={regra.regraId}
                    visible={expandedIds.includes(regra.regraId)}
                  />
                )}
              >
                <Table.Column
                  header="Tipo"
                  value={(regra: AnaliseInconsistenciaResumo) => (
                    <SeveridadeChip severidade={regra.severidade} />
                  )}
                />
                <Table.Column
                  header="Ocorrência"
                  value={(regra: AnaliseInconsistenciaResumo) => (
                    <>
                      {regra.descricao}
                      {regra.erro && (
                        <small className="typography-caption help-message">
                          <br />
                          {regra.erro}
                        </small>
                      )}
                    </>
                  )}
                />
                <Table.Column
                  header="Quantidade"
                  align="right"
                  value={(regra: AnaliseInconsistenciaResumo) => (
                    <strong>{regra.erro ? '-' : regra.quantidade}</strong>
                  )}
                />
                <Table.Column
                  header=""
                  value={(regra: AnaliseInconsistenciaResumo) => {
                    const aberto = expandedIds.includes(regra.regraId);

                    return (
                      <ActionButton
                        label={`Ver detalhes - ${regra.descricao}`}
                        icon={aberto ? 'chevron-up' : 'chevron-down'}
                        disabled={!!regra.erro}
                        onClick={() => handleExpandedIds(regra.regraId)}
                      />
                    );
                  }}
                />
              </Table>
            </Panel>
          )}
        </>
      )}
    </>
  );
};

export const AnaliseInconsistenciaPage = ({
  service,
  descricaoModulo
}: Props) => {
  const [regraEmEdicao, setRegraEmEdicao] = useState<string>();
  const [versaoRegras, setVersaoRegras] = useState(0);
  const [regrasVisitada, setRegrasVisitada] = useState(false);

  const concluirEdicao = (alterou: boolean) => {
    setRegraEmEdicao(undefined);
    if (alterou) {
      setVersaoRegras(versao => versao + 1);
    }
  };

  return (
    <Container breadcrumb>
      <Tabs
        renderAllContents
        onChangeTab={novaAba => {
          if (novaAba === 1) {
            setRegrasVisitada(true);
          }
          return true;
        }}
      >
        <Tabs.Tab header="Análise">
          <AnaliseInconsistenciaTab
            service={service}
            descricaoModulo={descricaoModulo}
            versaoRegras={versaoRegras}
          />
        </Tabs.Tab>
        <Tabs.Tab header="Regras">
          {!regrasVisitada ? null : regraEmEdicao ? (
            <RegraInconsistenciaComponent
              service={service}
              id={regraEmEdicao}
              onConcluir={concluirEdicao}
            />
          ) : (
            <RegrasInconsistenciaListComponent
              service={service}
              onNova={() => setRegraEmEdicao('new')}
              onEditar={setRegraEmEdicao}
              onExcluida={() => setVersaoRegras(versao => versao + 1)}
            />
          )}
        </Tabs.Tab>
      </Tabs>
    </Container>
  );
};
